import { Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { PROGRAM_PHASES } from "./homeContent";
import { Body, Card, CellGrid, H2, H3, Inner, Kicker, Lead, Mono, Section, alpha, useHomeTokens } from "./primitives";

/** #program — The Program, by invitation. Gold accent = `programGold` tokens. */
export const Program = () => {
  const t = useHomeTokens();
  return (
    <Section id="program">
      <Box
        position="absolute"
        top="-25%"
        left="-20%"
        right="-20%"
        h="700px"
        pointerEvents="none"
        bg={`radial-gradient(45% 45% at 50% 35%, ${alpha(t.gold, 0.14)} 0%, transparent 70%)`}
      />
      <Inner>
        <Flex wrap="wrap" align="flex-end" justify="space-between" gap="24px" mb="48px">
          <Flex direction="column" gap="18px" maxW="720px">
            <Flex
              align="center"
              gap="10px"
              alignSelf="flex-start"
              px="14px"
              py="7px"
              borderWidth="1px"
              borderColor={t.goldBorder}
              bg={t.goldTint}
              borderRadius="full"
            >
              <Box as="span" fontSize="13px" lineHeight="1" aria-hidden="true">
                💎
              </Box>
              <Kicker fontSize="11px" letterSpacing=".13em" color={t.gold}>
                The Program · by invitation
              </Kicker>
            </Flex>
            <H2>Run the platform as your own, and take a cut of what you sell</H2>
            <Lead>
              The Program is the mentor track. You get a sovereign build of your own, you are taught how to run it, and
              then you sell rungs forward under your own brand — the platform even carries your name rather than ours.
              There are no public pages: every stage after the invite sits behind a private link.
            </Lead>
          </Flex>
          <Mono fontSize="11.5px" color={alpha(t.text, 0.3)}>
            13 STAGES · INVITE ONLY
          </Mono>
        </Flex>

        <CellGrid columns={{ base: 1, md: 2, xl: 4 }}>
          {PROGRAM_PHASES.map((ph) => (
            <Flex key={ph.phase} direction="column" gap="13px" p="30px 26px" bg={t.bg} minW={0}>
              <Kicker fontSize="10.5px" letterSpacing=".13em" color={t.gold}>
                {ph.phase}
              </Kicker>
              <H3 fontSize="1.12rem">{ph.title}</H3>
              <Body fontSize=".9rem">{ph.body}</Body>
              <Box
                as="ul"
                listStyleType="none"
                m={0}
                p={0}
                display="grid"
                gap="7px"
                mt="6px"
                pt="14px"
                borderTop="1px solid"
                borderColor={t.hairline}
              >
                {ph.stages.map((st) => (
                  <Box as="li" key={st}>
                    <Mono fontSize="10.5px" letterSpacing=".08em" textTransform="uppercase" color={alpha(t.text, 0.35)}>
                      {st}
                    </Mono>
                  </Box>
                ))}
              </Box>
            </Flex>
          ))}
        </CellGrid>

        <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px" mt="20px">
          <Card
            p="28px"
            borderRadius="18px"
            gap="12px"
            borderColor={t.goldBorder}
            bg={`linear-gradient(135deg, ${t.goldTint} 0%, ${alpha(t.bg, 0.7)} 100%)`}
            _hover={{ borderColor: t.gold }}
          >
            <H3 fontSize="1.15rem">What you earn</H3>
            <Body color={t.textSoft}>
              Introduce a member and you earn{" "}
              <Text as="strong" color={t.goldBright} fontWeight="600">
                25% of the sale and 25% of ongoing revenue
              </Text>
              , recorded in an admin-confirmed commission ledger. On the FORK rung the sale is split 50/50 with you.
            </Body>
            <Body fontSize=".85rem" lineHeight="1.55" color={t.textFaint}>
              Level one only. A second level is not promised until the platform can trace it.
            </Body>
          </Card>
          <Card p="28px" borderRadius="18px" gap="12px">
            <H3 fontSize="1.15rem">It carries your brand</H3>
            <Body>
              The Program is renameable per tenant — the name, the logo and the tier pricing are yours to set. Your
              members never see ours.
            </Body>
          </Card>
          <Card p="28px" borderRadius="18px" gap="12px">
            <H3 fontSize="1.15rem">Your agreement is with TMS</H3>
            <Body>
              You introduce; delivery is between TMS and the member. You are not a party to it and carry no liability
              for it. That clause is written into every Program agreement in its own words.
            </Body>
          </Card>
        </SimpleGrid>

        <Body mt="26px" fontSize=".9rem" color={t.textFaint} maxW="820px">
          There is no application form on this page, by design. The Program runs on personal invitation — if you think
          you are the right fit, say so on a call and Tom will tell you honestly whether you are.
        </Body>
      </Inner>
    </Section>
  );
};
